/**
 * ARIA Jobs Page
 * Version: 1.0.0
 *
 * ARIA Feature implementations:
 * - ARIA-3: Jobs table columns (SE Number, Customer Name, Full Address,
 *           Appointment Description, Job Summary)
 * - ARIA-7: Identifier type (show None/TE/PA badge across job lists)
 * - Double-click to view job details
 */

import { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Search, Filter } from 'lucide-react';
import { clsx } from 'clsx';
import type { Job, JobStatus, JobType } from '../types';
import { IdentifierBadge } from '../components/jobs/IdentifierBadge';
import { JobDetailsModal } from '../components/jobs/JobDetailsModal';
import { SortableHeader } from '../components/table/SortableHeader';
import type { SortDirection } from '../utils/sort';

// GraphQL query for jobs list with ARIA-3 columns
const JOBS_QUERY = gql`
  query Jobs($page: Int, $pageSize: Int, $status: JobStatus, $identifierType: IdentifierType, $search: String, $sortField: String, $sortDirection: SortDirection) {
    jobs(page: $page, pageSize: $pageSize, status: $status, identifierType: $identifierType, search: $search, sortField: $sortField, sortDirection: $sortDirection) {
      items {
        id
        sytelineOrderId
        sytelineOrderNumber
        seNumber
        custPo
        referencedPaNumber
        quoteNumber
        quoteName
        jobType
        status
        priority
        identifierType
        customerName
        customerPhone
        fullAddress
        city
        province
        scheduling {
          scheduledDate
          startTime
          appointmentDescription
        }
        technician {
          id
          fullName
        }
        parts {
          status
          eta
        }
        description
        jobSummary
        createdAt
      }
      total
      page
      pageSize
    }
  }
`;

const statusConfig: Record<
  JobStatus,
  { label: string; color: string }
> = {
  PENDING_APPROVAL: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-700' },
  APPROVED: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  PENDING_PARTS: { label: 'Pending Parts', color: 'bg-orange-100 text-orange-700' },
  READY_TO_SCHEDULE: { label: 'Ready', color: 'bg-green-100 text-green-700' },
  SCHEDULED: { label: 'Scheduled', color: 'bg-indigo-100 text-indigo-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
  COMPLETED: { label: 'Completed', color: 'bg-gray-100 text-gray-700' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  ON_HOLD: { label: 'On Hold', color: 'bg-gray-100 text-gray-600' },
  PENDING_DEBRIEF: { label: 'Pending Debrief', color: 'bg-amber-100 text-amber-700' },
};

const typeLabels: Record<JobType, string> = {
  installation: 'Installation',
  service: 'Service',
  warranty: 'Warranty',
  inspection: 'Inspection',
  callback: 'Callback',
  estimate: 'Estimate',
};

type OrderTypeTab = 'ALL' | 'SE' | 'PA';

// SE Orders = jobs with no identifierType set (no active pipeline currently
// sets 'TE'). PA Orders = jobs synced from co_mst via upsert_pa_order_job.
const ORDER_TYPE_TAB_TO_FILTER: Record<OrderTypeTab, 'NONE' | 'PA' | undefined> = {
  ALL: undefined,
  SE: 'NONE',
  PA: 'PA',
};

export default function Jobs() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<JobStatus | ''>('');
  const [orderTypeTab, setOrderTypeTab] = useState<OrderTypeTab>('ALL');
  const [page, setPage] = useState(1);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirectionState] = useState<SortDirection>('asc');

  const handleSort = (field: string) => {
    setSortDirectionState((prev) => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(field);
    setPage(1);
  };

  const { data, loading, refetch } = useQuery(JOBS_QUERY, {
    variables: {
      page,
      pageSize: 20,
      status: filterStatus || undefined,
      identifierType: ORDER_TYPE_TAB_TO_FILTER[orderTypeTab],
      search: search || undefined,
      sortField: sortField || undefined,
      sortDirection: sortField ? sortDirection.toUpperCase() : undefined,
    },
  });

  const jobs = (data?.jobs?.items || []) as Job[];
  const total = data?.jobs?.total || 0;

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Service Orders</h1>
        <p className="text-gray-600">View and manage all service orders</p>
      </header>

      {/* Order type tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {(['ALL', 'SE', 'PA'] as OrderTypeTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setOrderTypeTab(tab);
              setPage(1);
            }}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
              orderTypeTab === tab
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {tab === 'ALL' ? 'All Orders' : tab === 'SE' ? 'SE Orders' : 'PA Orders'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order ID, customer name, or address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as JobStatus | '')}
            className="input pl-10 pr-8 appearance-none min-w-[180px]"
          >
            <option value="">All Statuses</option>
            {Object.entries(statusConfig).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Jobs Table - ARIA-3: Enhanced columns */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No jobs found</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <SortableHeader label="SE Number" field="seNumber" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Customer Name" field="customerName" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Full Address" field="fullAddress" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Appointment" field="appointment" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Job Summary" field="jobSummary" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                {/* ARIA-7: Identifier column */}
                <SortableHeader label="Type" field="jobType" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Status" field="status" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Technician" field="technician" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {jobs.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  onDoubleClick={() => setSelectedJobId(job.id)}
                />
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * 20 >= total}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Details Modal */}
      <JobDetailsModal
        jobId={selectedJobId || ''}
        isOpen={!!selectedJobId}
        onClose={() => setSelectedJobId(null)}
        onJobCompleted={refetch}
      />
    </div>
  );
}

// Job row component with ARIA-3 and ARIA-7 features
interface JobRowProps {
  job: Job;
  onDoubleClick: () => void;
}

function JobRow({ job, onDoubleClick }: JobRowProps) {
  const status = statusConfig[job.status] || { label: job.status, color: 'bg-gray-100 text-gray-700' };

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onDoubleClick={onDoubleClick}
      title="Double-click to view details"
    >
      {/* ARIA-3: SE Number */}
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{job.seNumber || job.sytelineOrderNumber}</div>
        <div className="text-xs text-gray-500">{job.sytelineOrderId}</div>
        {job.quoteName && <div className="text-xs text-gray-500">Quote: {job.quoteName}</div>}
        {job.quoteNumber && <div className="text-xs text-gray-500">Quote #: {job.quoteNumber}</div>}
        {job.referencedPaNumber && (
          <div className="text-xs text-gray-500">PA ref: {job.referencedPaNumber}</div>
        )}
      </td>

      {/* ARIA-3: Customer Name */}
      <td className="px-4 py-3">
        <div className="text-gray-900">{job.customerName || '-'}</div>
        {job.customerPhone && <div className="text-xs text-gray-500">{job.customerPhone}</div>}
        {job.custPo && <div className="text-xs text-gray-500">Cust PO: {job.custPo}</div>}
      </td>

      {/* ARIA-3: Full Address */}
      <td className="px-4 py-3">
        <div className="text-gray-900 max-w-xs truncate" title={job.fullAddress}>
          {job.fullAddress}
        </div>
      </td>

      {/* ARIA-3: Appointment Description */}
      <td className="px-4 py-3">
        <div className="text-gray-900">
          {job.scheduling?.appointmentDescription || job.description || '-'}
        </div>
        {job.scheduling?.scheduledDate && (
          <div className="text-xs text-gray-500">
            {new Date(job.scheduling.scheduledDate).toLocaleDateString()}
            {job.scheduling.startTime &&
              ` at ${new Date(job.scheduling.startTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}`}
          </div>
        )}
      </td>

      {/* ARIA-3: Job Summary */}
      <td className="px-4 py-3">
        <div className="text-gray-900 max-w-xs truncate" title={job.jobSummary || job.description}>
          {job.jobSummary || job.description || '-'}
        </div>
      </td>

      {/* ARIA-7: Identifier Type (TE/PA badge) */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'badge',
              job.jobType === 'installation' && 'badge-blue',
              job.jobType === 'service' && 'badge-green',
              job.jobType === 'warranty' && 'badge-purple',
              job.jobType === 'estimate' && 'badge-yellow',
              job.jobType === 'callback' && 'badge-red',
              job.jobType === 'inspection' && 'badge-gray'
            )}
          >
            {typeLabels[job.jobType] || job.jobType}
          </span>
          <IdentifierBadge type={job.identifierType} />
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span className={clsx('badge', status.color)}>{status.label}</span>
      </td>

      {/* Technician */}
      <td className="px-4 py-3">
        <div className="text-gray-900">{job.technician?.fullName || 'Unassigned'}</div>
      </td>
    </tr>
  );
}
